<template>
  <div class="colaborador-detail-page">
    <!-- Barra superior con título y acciones -->
    <v-card class="mb-4 elevation-2">
      <v-app-bar flat dense color="primary" dark>
        <v-btn icon @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>Detalles del Colaborador</v-toolbar-title>
        <v-spacer></v-spacer>

        <v-btn icon @click="showColaboradorForm(colaborador)" v-if="!loading">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </v-app-bar>
    </v-card>

    <!-- Estado de carga -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="50" width="5"></v-progress-circular>
      <div class="mt-4 text-subtitle-1 font-weight-medium grey--text text--darken-1">
        Cargando detalles del colaborador...
      </div>
    </div>

    <template v-else>
      <v-container>
        <v-row>
          <!-- Panel principal de información del colaborador -->
          <v-col cols="12" md="8" lg="7">
            <v-card class="elevation-1">
              <!-- Cabecera con datos principales -->
              <div class="colaborador-header pa-4">
                <div class="d-flex align-center">
                  <v-avatar color="primary" size="64" class="mr-4">
                    <v-icon size="36" dark>mdi-account-group</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-h5 font-weight-bold">{{ colaborador.name }}</div>

                  </div>
                </div>
              </div>

              <v-divider></v-divider>

              <!-- Información básica y de contacto -->
              <v-card-text class="py-4">
                <v-row>
                  <!-- Columna Información básica -->
                  <v-col cols="12" md="6">
                    <h3 class="subtitle-1 font-weight-bold mb-3">
                      <v-icon color="primary" small class="mr-2">mdi-information</v-icon>
                      Información básica
                    </h3>

                    <v-list dense class="transparent pa-0">
                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-account-group</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Nombre</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ colaborador.name }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0" v-if="colaborador.website">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-web</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Sitio web</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a :href="prependHttpIfNeeded(colaborador.website)" target="_blank" class="text-decoration-none">
                              {{ colaborador.website }}
                            </a>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-account-tie</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Manager</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ adminName || 'No asignado' }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0" v-if="colaborador.theme">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-palette</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Tema</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ getThemeLabel(colaborador.theme) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>

                  <!-- Columna Información de contacto -->
                  <v-col cols="12" md="6">
                    <h3 class="subtitle-1 font-weight-bold mb-3">
                      <v-icon color="primary" small class="mr-2">mdi-card-account-phone</v-icon>
                      Información de contacto
                    </h3>

                    <v-list dense class="transparent pa-0">
                      <v-list-item class="px-0" v-if="colaborador.address">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-map-marker</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Dirección</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ colaborador.address }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-phone</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Teléfono</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a v-if="colaborador.phone" :href="`tel:${colaborador.phone}`" class="text-decoration-none">
                              {{ colaborador.phone }}
                            </a>
                            <span v-else>No especificado</span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0" v-if="colaborador.mobile_phone">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-cellphone</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Teléfono móvil</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a :href="`tel:${colaborador.mobile_phone}`" class="text-decoration-none">
                              {{ colaborador.mobile_phone }}
                            </a>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-email</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Correo electrónico</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a v-if="colaborador.email" :href="`mailto:${colaborador.email}`" class="text-decoration-none">
                              {{ colaborador.email }}
                            </a>
                            <span v-else>No especificado</span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Panel lateral con logo y usuarios asociados -->
          <v-col cols="12" md="4" lg="5">
            <!-- Tarjeta de estadísticas -->
            <!-- <v-card class="elevation-1">
              <v-card-title class="pb-1"> -->
                <!-- <v-icon left color="primary">mdi-account-group</v-icon>
                Usuarios asociados -->
              <!-- </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="p-0"> -->
                <colaborador-users :colaborador-id="colaborador.id" />
              <!-- </v-card-text>
            </v-card> -->
          </v-col>
        </v-row>
      </v-container>
    </template>

    <!-- Formulario de edición -->
    <colaborador-form v-if="visibleColaboradorForm" v-model="editItem" @save="updateDataTable"
      @cancel="visibleColaboradorForm = false"></colaborador-form>
  </div>
</template>

<script>
import ColaboradorForm from './ColaboradorForm.vue'
import ColaboradorUsers from './ColaboradorUsers.vue'
import DealerService from '@/services/DealerService'
import UserService from '@/services/UserService'

export default {
  components: {
    ColaboradorForm,
    ColaboradorUsers
  },
  data () {
    return {
      loading: true,
      colaborador: null,
      visibleColaboradorForm: false,
      editItem: null,
      adminName: '',
      themes: [
        { value: 'light', text: 'Claro' },
        { value: 'dark', text: 'Oscuro' },
        { value: 'blue', text: 'Azul' },
        { value: 'green', text: 'Verde' },
        { value: 'red', text: 'Rojo' }
      ]
    }
  },
  async created () {
    await this.loadColaboradorDetails()
  },
  methods: {
    async loadColaboradorDetails () {
      try {
        this.loading = true
        const colaboradorId = this.$route.params.colaborador_id
        const { data } = await DealerService.view({ id: colaboradorId, expand: 'dealersAsociados' })
        this.colaborador = data.data

        // Obtener el nombre del administrador
        if (this.colaborador.admin_id) {
          const adminData = await UserService.viewUserDealer(this.colaborador.admin_id)
          this.adminName = `${adminData.data.data.name} ${adminData.data.data.lastname}`
        }
      } catch (error) {
        console.error('Error al cargar los detalles del colaborador:', error)
        this.$toast.error('Error al cargar los detalles del colaborador')
      } finally {
        this.loading = false
      }
    },

    showColaboradorForm (item) {
      this.editItem = item
      this.visibleColaboradorForm = true
    },

    updateDataTable () {
      this.visibleColaboradorForm = false
      this.loadColaboradorDetails()
      this.$toast.success('Colaborador actualizado con éxito')
    },

    getThemeLabel (themeValue) {
      const theme = this.themes.find(t => t.value === themeValue)
      return theme ? theme.text : themeValue
    },

    prependHttpIfNeeded (url) {
      if (!url) return '#'
      return url.match(/^https?:\/\//) ? url : `http://${url}`
    }
  }
}
</script>

<style scoped>
.colaborador-header {
  background-color: rgba(0, 0, 0, 0.01);
}

.v-list-item {
  min-height: 0;
  padding-top: 4px;
  padding-bottom: 4px;
}

/* Transiciones y efectos */
.v-card {
  transition: box-shadow 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}
</style>
